import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  WrenchScrewdriverIcon,
  GlobeAltIcon,
  PlayIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

function Tools() {
  const { can } = useAuth();
  const { t } = useTranslation();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [testingToolId, setTestingToolId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    webhook_url: '',
    webhook_timeout_seconds: 10,
    webhook_headers: {},
    parameters: {
      type: 'object',
      properties: {},
      required: []
    },
    is_active: true,
  });
  const [parameters, setParameters] = useState([]);
  const [headerKey, setHeaderKey] = useState('');
  const [headerValue, setHeaderValue] = useState('');
  const [generatorPrompt, setGeneratorPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      setLoading(true);
      const response = await api.get('/custom-tools');
      setTools(response.data);
    } catch (error) {
      console.error('Error fetching tools:', error);
      toast.error(t('errors.failedLoadTools'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      webhook_url: '',
      webhook_timeout_seconds: 10,
      webhook_headers: {},
      parameters: {
        type: 'object',
        properties: {},
        required: []
      },
      is_active: true,
    });
    setParameters([]);
    setHeaderKey('');
    setHeaderValue('');
    setEditingTool(null);
    setGeneratorPrompt('');
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (tool) => {
    setEditingTool(tool);
    setFormData({
      name: tool.name,
      description: tool.description,
      webhook_url: tool.webhook_url,
      webhook_timeout_seconds: tool.webhook_timeout_seconds,
      webhook_headers: tool.webhook_headers || {},
      parameters: tool.parameters || {
        type: 'object',
        properties: {},
        required: []
      },
      is_active: tool.is_active,
    });

    // Convert parameters to array format for editing
    const params = [];
    if (tool.parameters?.properties) {
      for (const [name, def] of Object.entries(tool.parameters.properties)) {
        params.push({
          name,
          type: def.type || 'string',
          description: def.description || '',
          required: tool.parameters.required?.includes(name) || false,
        });
      }
    }
    setParameters(params);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate tool name
    if (!formData.name || !/^[a-z][a-z0-9_]*$/.test(formData.name)) {
      toast.error(t('tools.toolNameValidation'));
      return;
    }

    // Build parameters JSON Schema from array
    const properties = {};
    const required = [];
    for (const param of parameters) {
      properties[param.name] = {
        type: param.type,
        description: param.description,
      };
      if (param.required) {
        required.push(param.name);
      }
    }

    const payload = {
      ...formData,
      parameters: {
        type: 'object',
        properties,
        required,
      },
    };

    try {
      if (editingTool) {
        await api.put(`/custom-tools/${editingTool.tool_id}`, payload);
        toast.success(t('success.toolUpdated'));
      } else {
        await api.post('/custom-tools', payload);
        toast.success(t('success.toolCreated'));
      }
      setShowModal(false);
      resetForm();
      fetchTools();
    } catch (error) {
      console.error('Error saving tool:', error);
      toast.error(error.response?.data?.detail || t('errors.failedSaveTool'));
    }
  };

  const handleDeleteTool = async (toolId) => {
    if (!window.confirm(t('tools.deleteConfirm'))) {
      return;
    }
    try {
      await api.delete(`/custom-tools/${toolId}`);
      toast.success(t('success.toolDeleted'));
      fetchTools();
    } catch (error) {
      console.error('Error deleting tool:', error);
      toast.error(error.response?.data?.detail || t('errors.failedDeleteTool'));
    }
  };

  const handleTestTool = async (toolId) => {
    setTestingToolId(toolId);
    setTestResult(null);
    try {
      const response = await api.post(`/custom-tools/${toolId}/test`);
      setTestResult(response.data);
      if (response.data.success) {
        toast.success(t('success.webhookTestOk'));
      } else {
        toast.error(`${t('tools.webhookTestFailed')}: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error testing tool:', error);
      setTestResult({ success: false, error: error.response?.data?.detail || 'Test failed' });
      toast.error(t('errors.failedTestWebhook'));
    } finally {
      setTestingToolId(null);
    }
  };

  const addParameter = () => {
    setParameters([...parameters, { name: '', type: 'string', description: '', required: false }]);
  };

  const updateParameter = (index, field, value) => {
    const updated = [...parameters];
    updated[index][field] = value;
    setParameters(updated);
  };

  const removeParameter = (index) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const addHeader = () => {
    if (headerKey && headerValue) {
      setFormData({
        ...formData,
        webhook_headers: {
          ...formData.webhook_headers,
          [headerKey]: headerValue,
        },
      });
      setHeaderKey('');
      setHeaderValue('');
    }
  };

  const removeHeader = (key) => {
    const headers = { ...formData.webhook_headers };
    delete headers[key];
    setFormData({ ...formData, webhook_headers: headers });
  };

  const handleGenerateTool = async () => {
    if (!generatorPrompt.trim() || generatorPrompt.length < 10) {
      toast.error(t('errors.descMinLength'));
      return;
    }

    setIsGenerating(true);
    try {
      const response = await api.post('/tool-generator', { prompt: generatorPrompt });

      if (response.data.success && response.data.schema) {
        const schema = response.data.schema;

        // Populate form data with generated schema
        setFormData({
          ...formData,
          name: schema.name || '',
          description: schema.description || '',
        });

        // Convert parameters to array format for the UI
        const params = [];
        if (schema.parameters?.properties) {
          for (const [name, def] of Object.entries(schema.parameters.properties)) {
            params.push({
              name,
              type: def.type || 'string',
              description: def.description || '',
              required: schema.parameters.required?.includes(name) || false,
            });
          }
        }
        setParameters(params);

        toast.success(t('success.toolGenerated'));
        setGeneratorPrompt('');
      } else {
        toast.error(t('errors.failedGenerateTool'));
      }
    } catch (error) {
      console.error('Error generating tool:', error);
      const errorMessage = error.response?.data?.detail || t('errors.failedGenerateTool');
      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between mb-8">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-foreground sm:text-3xl sm:truncate">
            {t('tools.title')}
          </h2>
          <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>
            {t('tools.subtitle')}
          </p>
        </div>
        {can('tools.create') && (
          <div className="mt-4 flex md:mt-0 md:ml-4">
            <button
              onClick={openCreateModal}
              className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              {t('tools.createTool')}
            </button>
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <div className="flex">
          <WrenchScrewdriverIcon className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-blue-800">{t('tools.howToolsWork')}</h3>
            <p className="mt-1 text-sm text-blue-700">
              {t('tools.toolsDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Tools Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : tools.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl shadow">
          <WrenchScrewdriverIcon className="mx-auto h-12 w-12" style={{ color: 'var(--muted)' }} />
          <h3 className="mt-2 text-sm font-medium text-foreground">{t('tools.noTools')}</h3>
          <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>{t('tools.getStarted')}</p>
          {can('tools.create') && (
            <div className="mt-6">
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-full text-white bg-primary hover:bg-primary-hover"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                {t('tools.createTool')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-card shadow overflow-hidden sm:rounded-xl">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-background">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                  {t('tools.tool')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                  {t('tools.webhookUrl')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                  {t('tools.parameters')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                  {t('common.status')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {tools.map((tool) => (
                <tr key={tool.tool_id}>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-xl bg-purple-100 flex items-center justify-center">
                          <WrenchScrewdriverIcon className="h-5 w-5 text-purple-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-foreground font-mono">{tool.name}</div>
                        <div className="text-sm max-w-xs truncate" style={{ color: 'var(--muted)' }}>{tool.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center text-sm" style={{ color: 'var(--muted)' }}>
                      <GlobeAltIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                      <span className="truncate max-w-[200px]" title={tool.webhook_url}>
                        {tool.webhook_url}
                      </span>
                    </div>
                    <div className="text-xs flex items-center mt-1" style={{ color: 'var(--muted)' }}>
                      <ClockIcon className="h-3 w-3 mr-1" />
                      {t('tools.timeout', { seconds: tool.webhook_timeout_seconds })}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm" style={{ color: 'var(--muted)' }}>
                      {t('tools.nParameters', { count: tool.parameters?.properties
                        ? Object.keys(tool.parameters.properties).length
                        : 0 })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        tool.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-secondary text-foreground'
                      }`}
                    >
                      {tool.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleTestTool(tool.tool_id)}
                      disabled={testingToolId === tool.tool_id}
                      className="text-green-600 hover:text-green-900 mr-3 disabled:opacity-50"
                      title={t('tools.testWebhook')}
                    >
                      {testingToolId === tool.tool_id ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                      ) : (
                        <PlayIcon className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => openEditModal(tool)}
                      className="text-primary-600 hover:text-primary-900 mr-3"
                      title={t('tools.editTool')}
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    {can('tools.delete') && (
                      <button
                        onClick={() => handleDeleteTool(tool.tool_id)}
                        className="text-red-600 hover:text-red-900"
                        title={t('tools.deleteTool')}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Test Result Display */}
      {testResult && (
        <div className={`mt-4 p-4 rounded-xl ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-start">
            {testResult.success ? (
              <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
            ) : (
              <ExclamationCircleIcon className="h-5 w-5 text-red-600 mr-2 flex-shrink-0" />
            )}
            <div className="flex-1">
              <h4 className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {testResult.success ? t('tools.webhookTestSuccess') : t('tools.webhookTestFailed')}
              </h4>
              {testResult.status_code && (
                <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>{t('tools.statusCode', { code: testResult.status_code })}</p>
              )}
              {testResult.error && (
                <p className="text-sm text-red-600 mt-1">{testResult.error}</p>
              )}
              {testResult.response_body && (
                <pre className="mt-2 text-xs bg-card p-2 rounded border border-border overflow-auto max-h-32">
                  {testResult.response_body}
                </pre>
              )}
              <button
                onClick={() => setTestResult(null)}
                className="mt-2 text-sm hover:opacity-70" style={{ color: 'var(--muted)' }}
              >
                {t('common.dismiss')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" style={{ backgroundColor: 'var(--overlay)' }} onClick={() => setShowModal(false)} />
            <div className="inline-block align-bottom bg-card rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button onClick={() => setShowModal(false)} className="hover:opacity-70" style={{ color: 'var(--muted)' }}>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-foreground mb-4">
                    {editingTool ? t('tools.editToolTitle') : t('tools.createToolTitle')}
                  </h3>

                  {/* AI Tool Generator - Only show when creating new tool */}
                  {!editingTool && (
                    <div className="bg-card border border-purple-200 rounded-xl p-4 mb-4">
                      <div className="flex items-start gap-3">
                        <SparklesIcon className="h-5 w-5 text-purple-600 flex-shrink-0 mt-1" />
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-purple-900 mb-2">{t('tools.aiToolGenerator')}</h4>
                          <p className="text-xs text-purple-700 mb-3">
                            {t('tools.aiToolGeneratorDesc')}
                          </p>
                          <textarea
                            value={generatorPrompt}
                            onChange={(e) => setGeneratorPrompt(e.target.value)}
                            placeholder="Example: Create a tool to log payment promises with customer_id, amount, promised_date, and optional notes"
                            rows={2}
                            className="w-full border border-purple-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 placeholder:text-muted"
                            disabled={isGenerating}
                          />
                          <button
                            type="button"
                            onClick={handleGenerateTool}
                            disabled={isGenerating || !generatorPrompt.trim()}
                            className="mt-2 inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-full text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isGenerating ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                {t('agentEditor.generating')}
                              </>
                            ) : (
                              <>
                                <SparklesIcon className="h-4 w-4 mr-2" />
                                {t('tools.generateSchema')}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground">{t('tools.toolName')}</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                          className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm font-mono"
                          placeholder="get_weather"
                        />
                        <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>{t('tools.toolNameHelp')}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground">{t('common.status')}</label>
                        <select
                          value={formData.is_active ? 'active' : 'inactive'}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                          className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
                        >
                          <option value="active">{t('common.active')}</option>
                          <option value="inactive">{t('common.inactive')}</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground">{t('tools.description')}</label>
                      <textarea
                        required
                        rows={2}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
                        placeholder="Get current weather information for a location"
                      />
                      <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>{t('tools.descriptionHelp')}</p>
                    </div>

                    {/* Webhook Settings */}
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-foreground mb-3">{t('tools.webhookConfig')}</h4>
                      <div>
                        <label className="block text-sm font-medium text-foreground">{t('tools.webhookUrlRequired')}</label>
                        <textarea
                          rows={2}
                          required
                          value={formData.webhook_url}
                          onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                          className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm font-mono resize-y overflow-y-auto"
                          placeholder="https://api.example.com/webhook"
                        />
                      </div>

                      <div className="mt-3">
                        <label className="block text-sm font-medium text-foreground">{t('tools.timeoutLabel')}</label>
                        <input
                          type="number"
                          min={1}
                          max={30}
                          value={formData.webhook_timeout_seconds}
                          onChange={(e) => setFormData({ ...formData, webhook_timeout_seconds: parseInt(e.target.value) || 10 })}
                          className="mt-1 block w-32 border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
                        />
                      </div>

                      {/* Headers */}
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-foreground mb-2">{t('tools.headersOptional')}</label>
                        {Object.entries(formData.webhook_headers).map(([key, value]) => (
                          <div key={key} className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-mono bg-secondary px-2 py-1 rounded">{key}</span>
                            <span className="text-sm truncate max-w-xs" style={{ color: 'var(--muted)' }}>{value}</span>
                            <button
                              type="button"
                              onClick={() => removeHeader(key)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={headerKey}
                            onChange={(e) => setHeaderKey(e.target.value)}
                            placeholder={t('tools.headerName')}
                            className="block w-40 border border-border rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-primary focus:border-primary-500"
                          />
                          <input
                            type="text"
                            value={headerValue}
                            onChange={(e) => setHeaderValue(e.target.value)}
                            placeholder={t('tools.headerValue')}
                            className="block flex-1 border border-border rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-primary focus:border-primary-500"
                          />
                          <button
                            type="button"
                            onClick={addHeader}
                            className="px-3 py-1 bg-secondary text-foreground rounded-full text-sm hover:opacity-80"
                          >
                            {t('common.add')}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Parameters */}
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-foreground">{t('tools.parameters')}</h4>
                        <button
                          type="button"
                          onClick={addParameter}
                          className="text-sm text-primary-600 hover:text-primary-700"
                        >
                          {t('tools.addParameter')}
                        </button>
                      </div>

                      {parameters.length === 0 ? (
                        <p className="text-sm" style={{ color: 'var(--muted)' }}>{t('tools.noParameters')}</p>
                      ) : (
                        <div className="space-y-3">
                          {parameters.map((param, index) => (
                            <div key={index} className="bg-background p-3 rounded-xl">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium" style={{ color: 'var(--muted)' }}>{t('tools.parameterN', { n: index + 1 })}</span>
                                <button
                                  type="button"
                                  onClick={() => removeParameter(index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                              <div className="grid grid-cols-3 gap-2">
                                <input
                                  type="text"
                                  value={param.name}
                                  onChange={(e) => updateParameter(index, 'name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                                  placeholder="name"
                                  className="block w-full border border-border rounded-md shadow-sm py-1 px-2 text-sm font-mono focus:outline-none focus:ring-primary focus:border-primary-500"
                                />
                                <select
                                  value={param.type}
                                  onChange={(e) => updateParameter(index, 'type', e.target.value)}
                                  className="block w-full border border-border rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-primary focus:border-primary-500"
                                >
                                  <option value="string">String</option>
                                  <option value="number">Number</option>
                                  <option value="integer">Integer</option>
                                  <option value="boolean">Boolean</option>
                                </select>
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={param.required}
                                    onChange={(e) => updateParameter(index, 'required', e.target.checked)}
                                    className="h-4 w-4 text-primary-600 focus:ring-primary border-border rounded"
                                  />
                                  <span className="ml-2 text-sm" style={{ color: 'var(--muted)' }}>{t('common.required')}</span>
                                </label>
                              </div>
                              <input
                                type="text"
                                value={param.description}
                                onChange={(e) => updateParameter(index, 'description', e.target.value)}
                                placeholder={t('tools.paramDescription')}
                                className="mt-2 block w-full border border-border rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-primary focus:border-primary-500"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Submit Buttons */}
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse pt-4 border-t">
                      <button
                        type="submit"
                        className="w-full inline-flex justify-center rounded-full border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        {editingTool ? t('tools.updateTool') : t('tools.createTool')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowModal(false)}
                        className="mt-3 w-full inline-flex justify-center rounded-full border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-background focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Tools;
